import React, { useState, useEffect, useMemo } from 'react';
import { onSystemUsersChange, updateSystemUser, removeSystemUser, updateUserRole, setUserHeadOfDepartments, normalizeHeadDepartmentsFromUser } from '../services/userDb';
import { useDepartments } from '../hooks/useDepartments.js';

export default function UserManagement({ showToast, currentUserEmail }) {
  const { departments } = useDepartments();
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('All Departments');
  const [roleFilter, setRoleFilter] = useState('All');
  const [loadingIds, setLoadingIds] = useState({});
  const [editUser, setEditUser] = useState(null); // user being edited
  const [editDept, setEditDept] = useState('');
  const [editHeadDepartments, setEditHeadDepartments] = useState([]);

  useEffect(() => {
    const unsubscribe = onSystemUsersChange(setEmployees);
    return unsubscribe;
  }, []);

  const openEdit = (user) => {
    setEditUser(user);
    setEditDept(user.department || '');
    setEditHeadDepartments(normalizeHeadDepartmentsFromUser(user));
  };

  const closeEdit = () => {
    setEditUser(null);
    setEditDept('');
    setEditHeadDepartments([]);
  };

  const handleSaveEdit = async () => {
    if (!editDept) { showToast('Please select a department', 'err'); return; }
    try {
      setLoadingIds(prev => ({ ...prev, [editUser.email]: true }));
      await updateSystemUser(editUser.email, editDept);
      if ((editUser.role || 'EMPLOYEE').toUpperCase() === 'EMPLOYEE') {
        await setUserHeadOfDepartments(editUser.email, editHeadDepartments);
      }
      showToast(`${editUser.name || editUser.email} updated successfully`, 'ok');
      closeEdit();
    } catch (e) {
      showToast('Failed to update', 'err');
    } finally {
      setLoadingIds(prev => ({ ...prev, [editUser.email]: false }));
    }
  };

  const handleRoleChange = async (user, newRole) => {
    const prev = (user.role || 'EMPLOYEE').toUpperCase();
    if (prev === newRole) return;
    if (currentUserEmail && user.email === currentUserEmail && newRole === 'EMPLOYEE') {
      if (!window.confirm('You are removing your own HR admin access. Continue?')) return;
    }
    try {
      setLoadingIds((prev) => ({ ...prev, [user.email]: true }));
      await updateUserRole(user.email, newRole);
      showToast(`Role set to ${newRole}`, 'ok');
    } catch (e) {
      console.error(e);
      showToast('Failed to update role', 'err');
    } finally {
      setLoadingIds((prev) => ({ ...prev, [user.email]: false }));
    }
  };

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove ${user.name || user.email}? This will revoke their access.`)) return;
    try {
      setLoadingIds(prev => ({ ...prev, [user.email]: true }));
      await removeSystemUser(user.email);
      showToast(`${user.email} removed.`, 'ok');
    } catch (e) {
      showToast('Failed to remove', 'err');
    } finally {
      setLoadingIds(prev => ({ ...prev, [user.email]: false }));
    }
  };

  const deptFilterOptions = useMemo(() => {
    const s = new Set(departments);
    employees.forEach((e) => {
      if (e.department) s.add(e.department);
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [departments, employees]);

  const editDeptOptions = useMemo(() => {
    const s = new Set(departments);
    if (editDept) s.add(editDept);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [departments, editDept]);

  const filtered = employees.filter((emp) => {
    const s = searchTerm.toLowerCase();
    const matchSearch =
      (emp.name || '').toLowerCase().includes(s) || (emp.email || '').toLowerCase().includes(s);
    const matchDept = deptFilter === 'All Departments' || emp.department === deptFilter;
    const r = (emp.role || 'EMPLOYEE').toUpperCase();
    const matchRole =
      roleFilter === 'All' ||
      (roleFilter === 'HR' && r === 'HR') ||
      (roleFilter === 'EMPLOYEE' && r === 'EMPLOYEE');
    return matchSearch && matchDept && matchRole;
  });

  const initials = (user) => ((user.name || user.email || 'U').substring(0, 2)).toUpperCase();

  const avatarColors = ['#3b5bdb', '#6d28d9', '#2a9d4f', '#d97706', '#c0392b', '#1a6db3', '#6b4f9e'];
  const avatarColor = (i) => avatarColors[i % avatarColors.length];

  return (
    <div className="pg">
      {/* Header */}
      <div className="ph" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700' }}>Employees</h1>
          <p style={{ color: 'var(--text2)', fontSize: '13px', marginTop: '3px' }}>
            {employees.length} user{employees.length !== 1 ? 's' : ''} in Firestore (HR + employees)
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', outline: 'none', cursor: 'pointer' }}
          >
            <option value="All">All roles</option>
            <option value="HR">HR only</option>
            <option value="EMPLOYEE">Employees only</option>
          </select>
          <select
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', outline: 'none', cursor: 'pointer' }}
          >
            <option>All Departments</option>
            {deptFilterOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '7px', border: '1px solid var(--border)', fontSize: '13px', width: '220px', outline: 'none' }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-sec, #f8f9fb)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'left' }}>Employee</th>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'left' }}>Role</th>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'left' }}>Department</th>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'left' }}>Dept head</th>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '13px 20px', fontSize: '11px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>
                  {employees.length === 0 ? '📭 No users yet.' : 'No users match your filters.'}
                </td>
              </tr>
            ) : filtered.map((u, i) => {
              const headedDepts = normalizeHeadDepartmentsFromUser(u);
              return (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                {/* Employee */}
                <td style={{ padding: '14px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '38px', height: '38px', borderRadius: '50%',
                      background: avatarColor(i) + '22', color: avatarColor(i),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: '700', flexShrink: 0
                    }}>
                      {initials(u)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>{u.name || 'New Employee'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{u.email}</div>
                    </div>
                  </div>
                </td>

                {/* Role */}
                <td style={{ padding: '14px 20px' }}>
                  <select
                    value={(u.role || 'EMPLOYEE').toUpperCase()}
                    onChange={(e) => handleRoleChange(u, e.target.value)}
                    disabled={loadingIds[u.email]}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: '1px solid var(--border)',
                      background: 'var(--surface)',
                      cursor: loadingIds[u.email] ? 'wait' : 'pointer',
                    }}
                  >
                    <option value="EMPLOYEE">EMPLOYEE</option>
                    <option value="HR">HR</option>
                  </select>
                </td>

                {/* Department */}
                <td style={{ padding: '14px 20px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                    background: 'var(--accent-bg)', color: 'var(--accent)'
                  }}>
                    {u.department || '—'}
                  </span>
                </td>

                <td style={{ padding: '14px 20px', fontSize: '12px', color: 'var(--text2)' }}>
                  {headedDepts.length ? (
                    <span style={{ fontWeight: '600', color: 'var(--purple)' }} title={headedDepts.join(', ')}>
                      Head: {headedDepts.join(', ')}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>

                {/* Status */}
                <td style={{ padding: '14px 20px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                    background: '#edfaf2', color: '#2a9d4f', border: '1px solid #c3e6cb'
                  }}>
                    ACTIVE
                  </span>
                </td>

                {/* Actions */}
                <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => openEdit(u)}
                      disabled={loadingIds[u.email]}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                        background: 'var(--accent-bg)', color: 'var(--accent)',
                        border: '1px solid rgba(59,91,219,0.2)', cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemove(u)}
                      disabled={loadingIds[u.email]}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                        background: '#fef2f2', color: '#dc2626',
                        border: '1px solid #fee2e2', cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(3px)', zIndex: 300,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={closeEdit}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '14px', width: '420px',
              padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>Edit Employee</h3>
              <p style={{ fontSize: '13px', color: 'var(--text2)' }}>{editUser.email}</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text2)', marginBottom: '6px' }}>Department</label>
              <select
                value={editDept}
                onChange={e => setEditDept(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--border)', fontSize: '14px', outline: 'none',
                  background: 'var(--surface2)'
                }}
              >
                <option value="" disabled>Choose department...</option>
                {editDeptOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {(editUser.role || 'EMPLOYEE').toUpperCase() === 'EMPLOYEE' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text2)', marginBottom: '6px' }}>
                  Department head (optional)
                </label>
                <p style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '8px', lineHeight: 1.4 }}>
                  Select one or more departments this person heads. They approve leave requests from those departments first.
                </p>
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    background: 'var(--surface2)',
                  }}
                >
                  {departments.length === 0 ? (
                    <span style={{ fontSize: '12px', color: 'var(--text3)' }}>No departments in settings.</span>
                  ) : (
                    departments.map((d) => (
                      <label
                        key={d}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 4px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editHeadDepartments.includes(d)}
                          onChange={() => {
                            setEditHeadDepartments((prev) =>
                              prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
                            );
                          }}
                        />
                        <span>{d}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={closeEdit}
                style={{
                  padding: '9px 20px', borderRadius: '7px', fontSize: '13px', fontWeight: '600',
                  background: 'var(--surface2)', color: 'var(--text)',
                  border: '1px solid var(--border)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editDept || loadingIds[editUser?.email]}
                style={{
                  padding: '9px 20px', borderRadius: '7px', fontSize: '13px', fontWeight: '600',
                  background: 'var(--accent)', color: 'white',
                  border: 'none', cursor: 'pointer', opacity: !editDept ? 0.5 : 1
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
